import FeatureBento from '../../components/stageshow/FeatureBento';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/common/SEO';
import { useAuth } from '../../contexts/AuthContext';
import '../landing/landing.css';
import './developers.css';
import {
  API_BASE, ENDPOINTS, ERROR_CODES, EXEMPTION_RESPONSE, FREE_MONTHLY_CALCS, MCP_TOOLS, MCP_URL, PIPX_INSTALL,
  SAMPLE_RESPONSE, SnippetLang, calculateSnippet, claudeCodeCommand, exemptionSnippet, exemptionValidateSnippet,
  mcpHostedConfig, mcpStdioConfig, upgradeMailto,
} from './snippets';
import { copyText } from './copyText';

const FREE = FREE_MONTHLY_CALCS.toLocaleString('en-US');

const Code: React.FC<{ code: string; label: string }> = ({ code, label }) => {
  const [copied, setCopied] = useState(false);
  return (
    <div className="dv-code">
      <button
        type="button"
        className="dv-copy"
        aria-label={`Copy ${label}`}
        onClick={async () => {
          if (await copyText(code)) {
            setCopied(true);
            setTimeout(() => setCopied(false), 1800);
          }
        }}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
      <pre><code>{code}</code></pre>
    </div>
  );
};

const practices = [
  {
    title: 'Idempotent by design',
    text: 'Send an Idempotency-Key with each order. A retry returns the stored result, with no second engine call and no second charge against your quota.',
  },
  {
    title: 'Quota in every response',
    text: 'X-Quota-Limit and X-Quota-Remaining come back on billable calls, so you can see where you are before the month runs out.',
  },
  {
    title: 'Test keys that never bill',
    text: 'gtt_test_ keys run on the GoTruTax engine with state base rates, so you can build and run CI without spending live calculations.',
  },
  {
    title: 'One error shape',
    text: 'Every failure returns {"error": {type, code, message}} with a request id. 401 bad key, 402 free tier used, 422 invalid cart, 429 slow down, 502 engine error (not counted).',
  },
];

const langs: { id: SnippetLang; label: string }[] = [
  { id: 'curl', label: 'curl' },
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
];

export const DevelopersPage: React.FC = () => {
  const { user } = useAuth();
  const [lang, setLang] = useState<SnippetLang>('curl');
  const [exLang, setExLang] = useState<SnippetLang>('curl');
  const [mcp, setMcp] = useState<'hosted' | 'claude' | 'stdio'>('hosted');
  const primary = user
    ? { to: '/developers/console', label: 'Get an API key' }
    : { to: '/register', label: 'Create a free account' };

  return (
    <>
      <SEO
        title="Sales Tax API and MCP Server for Developers | GoTruTax"
        description={`Calculate US sales tax, look up rates by ZIP code and check economic nexus with the GoTruTax REST API or MCP server for AI agents. ${FREE} live calculations a month free.`}
        keywords={['sales tax API', 'sales tax calculation API', 'tax rate API', 'economic nexus API', 'MCP server', 'AI agent sales tax', 'Model Context Protocol']}
        url="https://gotrutax.com/developers"
        canonical="https://gotrutax.com/developers"
        type="website"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebAPI',
          name: 'GoTruTax Sales Tax API',
          description: 'REST API and MCP server for US sales tax calculation, rates by ZIP code and economic nexus checks.',
          url: 'https://gotrutax.com/developers',
          documentation: 'https://gotrutax.com/developers',
          provider: { '@type': 'Organization', name: 'GoTruTax', url: 'https://gotrutax.com' },
          offers: { '@type': 'Offer', price: '0', priceCurrency: 'USD', description: `${FREE} live calculations per month free` },
        }}
      />

      <div className="gtl dv" data-theme="furrow">
        {/* Hero: Stage, a real request and its response */}
        <section className="hero dv-hero" aria-labelledby="dv-title">
          <div className="container hero-grid">
            <div className="hero-copy">
              <p className="dv-eyebrow">GoTruTax for developers</p>
              <h1 id="dv-title">Sales tax in your checkout, and in your agents.</h1>
              <p className="lead">A REST API and an MCP server for US sales tax. Calculate tax on a cart, look up rates by ZIP code, and check economic nexus by state.</p>
              <div className="hero-actions">
                <Link className="key key-primary" to={primary.to}>{primary.label}</Link>
                <a className="key key-quiet" href="#endpoints">See the endpoints</a>
                <Link className="key key-quiet" to="/developers/checkout" data-testid="dev-checkout-link">Checkout widget</Link>
              </div>
              <p className="hero-terms">{FREE} live calculations a month are free. Test keys are never billed.</p>
            </div>

            <figure className="hero-visual dv-visual" aria-label="A sales tax calculation request and its response">
              <div className="dv-pane">
                <div className="dv-pane-head"><span className="dv-verb">POST</span><span>/api/v1/public/calculate</span></div>
                <pre className="dv-pre"><code>{`{
  "line_items": [{
    "reference": "sku_tshirt",
    "unit_amount": 2500,
    "quantity": 2,
    "tax_code": "txcd_99999999"
  }],
  "ship_to": {
    "city": "Indianapolis",
    "state": "IN",
    "postal_code": "46204"
  }
}`}</code></pre>
              </div>
              <div className="dv-pane dv-pane-out">
                <div className="dv-pane-head"><span className="dv-status">200</span><span>application/json</span></div>
                <pre className="dv-pre"><code>{SAMPLE_RESPONSE}</code></pre>
              </div>
              <figcaption className="dv-caption">Response from a test key, abridged. Amounts are in cents.</figcaption>
            </figure>
          </div>
        </section>
        <FeatureBento variant="developers" />

        {/* Endpoints: Index */}
        <section id="endpoints" className="band-sources dv-endpoints" aria-labelledby="ep-title">
          <div className="container sources-grid">
            <div className="dv-ep-head">
              <h2 id="ep-title">{ENDPOINTS.length} endpoints</h2>
              <p className="dv-muted">Base URL <code className="dv-inline">{API_BASE}</code>. Authenticate with <code className="dv-inline">Authorization: Bearer gtt_live_…</code>. Full schemas are in the <a href="/docs">OpenAPI reference</a>.</p>
            </div>
            <table className="sources dv-table">
              <thead>
                <tr><th scope="col">Endpoint</th><th scope="col">What it does</th><th scope="col">Counts toward quota</th></tr>
              </thead>
              <tbody>
                {ENDPOINTS.map((e) => (
                  <tr key={e.path}>
                    <th scope="row"><span className="dv-method">{e.method}</span> <code>{e.path}</code></th>
                    <td>{e.summary}</td>
                    <td>{e.billable ? 'Yes' : 'No, free'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Code samples: Split */}
        <section className="band-calc dv-samples" aria-labelledby="code-title">
          <div className="container calc-grid">
            <div className="dv-samples-copy">
              <h2 id="code-title">Tax a cart in one request</h2>
              <p className="lead">Each line is taxed by its product tax code at the ship-to address. You get tax per line and per jurisdiction, and the lines add up to the total.</p>
              <p className="dv-muted">Omit <code className="dv-inline">tax_code</code> and pass a <code className="dv-inline">description</code> to have one inferred. Pass explicit codes when you need repeatable results.</p>
            </div>
            <div className="dv-samples-code">
              <div className="dv-tabs" role="tablist" aria-label="Code sample language">
                {langs.map((l) => (
                  <button key={l.id} type="button" role="tab" aria-selected={lang === l.id} className={`dv-tab${lang === l.id ? ' is-on' : ''}`} onClick={() => setLang(l.id)}>
                    {l.label}
                  </button>
                ))}
              </div>
              <Code code={calculateSnippet(lang)} label={`${lang} sample`} />
            </div>
          </div>
        </section>

        {/* Exemptions: Split */}
        <section id="exemptions" className="band-calc dv-samples" aria-labelledby="exemptions-title">
          <div className="container calc-grid">
            <div className="dv-samples-copy">
              <h2 id="exemptions-title">Apply a customer's exemption certificate</h2>
              <p className="lead">Store the certificate once, then send the customer id with the cart. If an approved certificate covers the ship-to state and is not expired on the calculation date, tax is removed automatically.</p>
              <p className="dv-muted">The response keeps the engine's per-line detail, adds <code className="dv-inline">exempt_amount</code> (the tax that was exempted, in cents) and an <code className="dv-inline">exemption</code> block with the <code className="dv-inline">certificate_id</code> and <code className="dv-inline">expiry_status</code>: <code className="dv-inline">valid</code>, <code className="dv-inline">expiring_soon</code> (within 30 days) or <code className="dv-inline">expired</code>.</p>
              <p className="dv-muted">No certificate on file? The response still returns real tax, with <code className="dv-inline">exemption.applied: false</code> so you can decide whether to ask the buyer for one. Send <code className="dv-inline">customer_id</code> with a key that belongs to a company.</p>
            </div>
            <div className="dv-samples-code">
              <div className="dv-tabs" role="tablist" aria-label="Exemption sample language">
                {langs.map((l) => (
                  <button key={l.id} type="button" role="tab" aria-selected={exLang === l.id} className={`dv-tab${exLang === l.id ? ' is-on' : ''}`} onClick={() => setExLang(l.id)}>
                    {l.label}
                  </button>
                ))}
              </div>
              <Code code={exemptionSnippet(exLang)} label={`${exLang} exemption sample`} />
              <Code code={EXEMPTION_RESPONSE} label="exemption response" />
              <p className="dv-muted dv-note">Check a certificate before relying on it with <code className="dv-inline">GET /exemption-certificates/:id/validate</code>. It is free and never returns the uploaded document.</p>
              <Code code={exemptionValidateSnippet(exLang)} label={`${exLang} certificate validation sample`} />
            </div>
          </div>
        </section>

        {/* MCP: Split */}
        <section id="mcp" className="band-rows dv-mcp" aria-labelledby="mcp-title">
          <div className="container rows-grid">
            <div className="rows-head">
              <h2 id="mcp-title">MCP for AI agents</h2>
              <p className="rows-aside">
                Point Cursor, Claude or any MCP client at <code className="dv-inline">{MCP_URL}</code>. Agents get the same answers as the REST API, with the same key, quota and limits.
              </p>
              <ul className="dv-tools">
                {MCP_TOOLS.map((t) => (
                  <li key={t.name}><code>{t.name}</code><span>{t.summary}</span></li>
                ))}
              </ul>
            </div>
            <div className="dv-mcp-code">
              <div className="dv-tabs" role="tablist" aria-label="MCP client">
                <button type="button" role="tab" aria-selected={mcp === 'hosted'} className={`dv-tab${mcp === 'hosted' ? ' is-on' : ''}`} onClick={() => setMcp('hosted')}>Hosted (HTTP)</button>
                <button type="button" role="tab" aria-selected={mcp === 'claude'} className={`dv-tab${mcp === 'claude' ? ' is-on' : ''}`} onClick={() => setMcp('claude')}>Claude Code</button>
                <button type="button" role="tab" aria-selected={mcp === 'stdio'} className={`dv-tab${mcp === 'stdio' ? ' is-on' : ''}`} onClick={() => setMcp('stdio')}>Local (stdio)</button>
              </div>
              {mcp === 'hosted' && <Code code={mcpHostedConfig()} label="hosted MCP config" />}
              {mcp === 'claude' && <Code code={claudeCodeCommand()} label="Claude Code command" />}
              {mcp === 'stdio' && (
                <>
                  <Code code={PIPX_INSTALL} label="install command" />
                  <Code code={mcpStdioConfig()} label="stdio MCP config" />
                </>
              )}
              <p className="dv-muted dv-note">Tool errors, such as a used-up free tier, come back as readable results, so the agent can tell you what happened.</p>
            </div>
          </div>
        </section>

        {/* Production details: Rows */}
        <section className="band-sources dv-practices" aria-labelledby="prod-title">
          <div className="container">
            <h2 id="prod-title">Made for production checkouts</h2>
            <ol className="dv-grid">
              {practices.map((p, i) => (
                <li key={p.title}>
                  <span className="rows-n num">{String(i + 1).padStart(2, '0')}</span>
                  <h3>{p.title}</h3>
                  <p>{p.text}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Errors: Rows */}
        <section id="errors" className="band-sources dv-practices" aria-labelledby="errors-title">
          <div className="container">
            <h2 id="errors-title">Error codes</h2>
            <p className="dv-muted">Every failure returns the same envelope: <code className="dv-inline">{'{"error": {"type", "code", "message", "param"}, "request_id": "req_..."}'}</code>. Branch on <code className="dv-inline">error.code</code>, not just the status.</p>
            <table className="sources dv-table">
              <thead>
                <tr><th scope="col">HTTP</th><th scope="col">Codes</th><th scope="col">What to do</th></tr>
              </thead>
              <tbody>
                {ERROR_CODES.map((e) => (
                  <tr key={e.status}>
                    <th scope="row"><span className="dv-status">{e.status}</span></th>
                    <td><code>{e.codes}</code></td>
                    <td>{e.meaning}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* Free tier: Offer */}
        <section id="free-tier" className="band-pricing dv-tier" aria-labelledby="tier-title">
          <div className="container">
            <div className="pricing-head">
              <h2 id="tier-title">Free to start</h2>
              <p className="pricing-sub">Every account gets {FREE} live calculations a month. Usage resets on the 1st (UTC).</p>
            </div>
            <div className="dv-tier-grid">
              <article className="plan">
                <h3>Free tier</h3>
                <p className="plan-price"><span className="num">{FREE}</span><span className="plan-cadence">calculations / month</span></p>
                <ul className="plan-list">
                  <li>Calculate and rates calls count; nexus checks and usage lookups don’t</li>
                  <li>Test keys for development: up to {FREE} calls a month, never billed</li>
                  <li>REST API and hosted MCP server</li>
                  <li>Up to 10 requests per second per key</li>
                </ul>
                <Link className="key key-primary plan-cta" to={primary.to}>{primary.label}</Link>
              </article>
              <article className="plan">
                <h3>Paid usage</h3>
                <p className="dv-tier-text">Need more than {FREE} a month? With paid usage, live calls keep working past the free tier instead of returning 402. Tell us your expected volume and we’ll set it up.</p>
                <a className="key key-quiet plan-cta" href={upgradeMailto()}>Contact us about paid usage</a>
              </article>
            </div>
          </div>
        </section>

        {/* Closing band */}
        <section className="band-close" aria-labelledby="dv-close-title">
          <div className="container">
            <div className="close-grid">
              <h2 id="dv-close-title">Get a test key and send your first cart.</h2>
              <Link className="key key-primary" to={primary.to}>{primary.label}</Link>
            </div>
            <ol className="close-steps">
              <li><span className="num">1</span>{user ? 'Open the Developers console' : 'Create a free GoTruTax account'}</li>
              <li><span className="num">2</span>Create a test key and copy it. It’s shown once.</li>
              <li><span className="num">3</span>POST a cart to /calculate, or add the MCP server to your agent</li>
            </ol>
          </div>
        </section>
      </div>
    </>
  );
};

export default DevelopersPage;
